/**
 * Générateur — Il calcule le volume d'une boule.
 * Attendu ID : 231 | Niveau : 3ème | Domaine : Grandeurs (d18, O1)
 *
 * Source doc prof (GRANDEURS ET MESURES 3e, séance CS1). Plan auto-validé
 * 2026-07-17 (TODO §6) : « boule » (V exact en coef de π + arrondi, le double
 * piège diamètre→rayon), « verre » (cône h = r vs sphère : 4 verres, calcul
 * littéral sans valeurs), « cloche » (demi-sphère = cylindre → retrouver h).
 * Convention 5e/4e : le COEFFICIENT de π vit dans un trou séparé, π n'est
 * jamais tapé ; arrondis PARTAGÉS gen↔solveur (garde anti-pivot).
 */

import { randomChoice, randInt, tex, makeTexteTrou } from '../../../utils';

const ATTENDU_ID = 231;
const L_BOULE = 'Calculer le volume d\'une boule (exact et arrondi)';
const L_VERRE = 'Comparer cône et boule sans calculatrice';
const L_CLOCHE = 'De la demi-sphère au cylindre : retrouver une hauteur';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Arrondi entier PARTAGÉ, refusé près d'un pivot (re-tirage côté gen).
const arrondiSur = (x) => {
    const frac = x - Math.floor(x);
    if (Math.abs(frac - 0.5) < 0.03) return null;
    return Math.round(x);
};

// ═════════ « boule » ═════════

const genBouleTrous = () => {
    let r; let coef3; let approx;
    for (let essai = 0; essai < 80; essai++) {
        r = randInt(4, 15);                                // rayon en cm
        coef3 = 4 * r * r * r;                             // V = (coef3/3)·π cm³
        approx = arrondiSur((coef3 * Math.PI) / 3);
        if (approx !== null) break;
        approx = null;
    }
    if (approx === null) { r = 9; coef3 = 2916; approx = 3054; }
    const d = 2 * r;
    const objet = randomChoice(['Une boule de pétanque géante', 'Un ballon sphérique', 'Une bille géante', 'Une sphère décorative']);
    const phrase = `$V = \\dfrac{{{${coef3}}}}{3} \\pi\\ \\text{cm}^{3} \\approx {{${approx}}}\\ \\text{cm}^{3}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${objet} a un DIAMÈTRE de ${d} cm.</p>`
        // « et le rayon vaut la moitié du diamètre ! » désamorçait le piège de
        // l'énoncé (diamètre donné, rayon attendu). La formule reste, elle est
        // au programme et se lit dans le formulaire du brevet.
        + `<p>Le volume d'une boule de rayon r vaut ${tex('\\dfrac{4}{3} \\pi r^{3}')}.</p>`
        + '<p><b>Donne le volume exact en complétant le coefficient de π, puis l\'arrondi '
        + 'au cm³.</b></p>',
        { ...opts(L_BOULE), mode: 'saisie' });
    payload._meta.params = { r, d, coef3, approx };
    return payload;
};

// ═════════ « verre » — cône vs boule/demi-boule ═════════

const genVerreTrous = () => {
    const demi = randInt(0, 1) === 1;
    const nb = demi ? 2 : 4;
    const recipient = demi ? 'un récipient DEMI-sphérique' : 'un récipient sphérique';
    const contenant = randomChoice(['Un verre conique', 'Un entonnoir conique (bouché)', 'Un cornet conique']);
    const r = randInt(4, 12);                              // valeur d'habillage : le résultat n'en dépend PAS
    const phrase = `Il faut {{${nb}}} ${contenant.includes('verre') ? 'verres' : (contenant.includes('entonnoir') ? 'entonnoirs' : 'cornets')}.`;
    const payload = makeTexteTrou(phrase,
        `<p>${contenant} a une hauteur égale à son rayon r = ${r} cm ; son volume vaut donc `
        + `${tex('\\dfrac{\\pi r^{3}}{3}')}. On veut remplir ${recipient} de MÊME rayon. `
        + `Le volume d'une boule est ${tex('\\dfrac{4}{3} \\pi r^{3}')}.</p>`
        // « le rayon se simplifie, le résultat ne dépend pas de sa valeur ! »
        // était la découverte visée par l'exercice.
        + '<p><b>Combien de fois faut-il le vider ?</b></p>',
        { ...opts(L_VERRE), mode: 'saisie' });
    payload._meta.params = { demi, nb, r };
    return payload;
};

// ═════════ « cloche » — demi-sphère = cylindre → h ═════════

const genClocheTrous = () => {
    const r = randomChoice([3, 6, 9, 12, 15, 18, 21]);     // multiple de 3 → h entier
    const objet = randomChoice(['Une cloche à fromage', 'Un saladier renversé', 'Un dôme décoratif']);
    const coefDemi = (2 * r * r * r) / 3;                  // demi-sphère = (2/3)πr³
    const h = (2 * r) / 3;
    const phrase = `$\\text{Volume du solide : } {{${coefDemi}}} \\pi\\ \\text{cm}^{3}`
        + ` \\qquad \\text{hauteur de la boîte : } {{${h}}}\\ \\text{cm}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${objet} DEMI-sphérique de rayon ${r} cm et une boîte cylindrique de même rayon `
        + 'ont exactement le même volume.</p>'
        + '<p><b>Donne le volume du solide demi-sphérique en complétant le coefficient de π, '
        + 'puis déduis-en la hauteur de la boîte.</b></p>',
        { ...opts(L_CLOCHE), mode: 'saisie' });
    payload._meta.params = { r, coefDemi, h };
    return payload;
};

const GENERATORS = {
    boule_texte_trou_saisie: genBouleTrous,
    verre_texte_trou_saisie: genVerreTrous,
    cloche_texte_trou_saisie: genClocheTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'boule', label: L_BOULE, formatsList: buildFormats('boule') },
    { id: 'verre', label: L_VERRE, formatsList: buildFormats('verre') },
    { id: 'cloche', label: L_CLOCHE, formatsList: buildFormats('cloche') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
